import { INCOME_SUBGROUP_CATEGORY } from 'src/Enum/IncomeStructure';
import {
  IIncomeItemFormValues,
  IIncomeStructureFormValues,
  IIncomeCategoryFormValues,
} from '../NatureOfBusinessFormProvider/types';
import { IInfoCategoryMasterData } from '@Domains/MasterData';
import {
  IncomeCategorySchema,
  IncomeStructureSchema,
  IntroductionSchema,
  RemarkSchema,
} from './validators';
import {
  checkAssetsValidEn,
  checkAssetsValidTh,
} from '@Containers/UploadImageFormFields/utils';
import {
  IntroductionFormValues,
  RemarkFormValues,
} from '@Utils/Types/FormValuesTypes';

export const yearFields: (keyof IIncomeItemFormValues)[] = [
  'asOfYearBeforeYesteryear',
  'asOfYesteryear',
  'asOfYear',
];

export const filterAndSortBySubgroup = (
  items: Record<string, IIncomeItemFormValues>,
  subgroup: INCOME_SUBGROUP_CATEGORY
) => {
  return Object.entries(items)
    .filter(([, value]) => value.incomeCategory?.subGroup === subgroup)
    .sort(
      ([, prev], [, next]) =>
        (prev.incomeCategory?.sequence ?? Infinity) -
        (next.incomeCategory?.sequence ?? Infinity)
    );
};

export const findMasterDataById = (
  data: IInfoCategoryMasterData[],
  id: number
): IInfoCategoryMasterData | undefined => {
  return data?.find((item) => item.id === id);
};

export const mapIncomeCategoryToFormValues = (
  data?: IIncomeCategoryFormValues
) => {
  return {
    nameTh: data?.nameTh,
    nameEn: data?.nameEn,
  };
};

export const updateIncomeCategory = (
  formValues: IIncomeCategoryFormValues,
  prevIncomeCategory?: IIncomeCategoryFormValues
) => {
  return {
    ...prevIncomeCategory,
    nameTh: formValues.nameTh,
    nameEn: formValues.nameEn,
  };
};

const checkNameValidTh = (incomeStructure?: IIncomeStructureFormValues) => {
  const { productIncomeItems, otherIncomeItems } =
    incomeStructure as IIncomeStructureFormValues;

  const productNameThCompleted = filterAndSortBySubgroup(
    productIncomeItems,
    INCOME_SUBGROUP_CATEGORY.PRODUCT_INCOME
  ).every(([, value]) =>
    checkIncomeCategoryNameCompleted(value.incomeCategory, 'th')
  );

  const otherOperationNameThCompleted = filterAndSortBySubgroup(
    otherIncomeItems,
    INCOME_SUBGROUP_CATEGORY.OTHER_OPERATION_INCOME
  ).every(([, value]) =>
    checkIncomeCategoryNameCompleted(value.incomeCategory, 'th')
  );

  const otherNonOperationNameThCompleted = filterAndSortBySubgroup(
    otherIncomeItems,
    INCOME_SUBGROUP_CATEGORY.OTHER_NON_OPERATION_INCOME
  ).every(([, value]) =>
    checkIncomeCategoryNameCompleted(value.incomeCategory, 'th')
  );

  return (
    productNameThCompleted &&
    otherOperationNameThCompleted &&
    otherNonOperationNameThCompleted
  );
};

const checkNameValidEn = (incomeStructure?: IIncomeStructureFormValues) => {
  const { productIncomeItems, otherIncomeItems } =
    incomeStructure as IIncomeStructureFormValues;

  const productNameEnCompleted = filterAndSortBySubgroup(
    productIncomeItems,
    INCOME_SUBGROUP_CATEGORY.PRODUCT_INCOME
  ).every(([, value]) =>
    checkIncomeCategoryNameCompleted(value.incomeCategory, 'en')
  );

  const otherOperationNameEnCompleted = filterAndSortBySubgroup(
    otherIncomeItems,
    INCOME_SUBGROUP_CATEGORY.OTHER_OPERATION_INCOME
  ).every(([, value]) =>
    checkIncomeCategoryNameCompleted(value.incomeCategory, 'en')
  );

  const otherNonOperationNameEnCompleted = filterAndSortBySubgroup(
    otherIncomeItems,
    INCOME_SUBGROUP_CATEGORY.OTHER_NON_OPERATION_INCOME
  ).every(([, value]) =>
    checkIncomeCategoryNameCompleted(value.incomeCategory, 'en')
  );

  return (
    productNameEnCompleted &&
    otherOperationNameEnCompleted &&
    otherNonOperationNameEnCompleted
  );
};

const checkIncomeStructureCompleted = (
  incomeStructure?: IIncomeStructureFormValues
): boolean => {
  const formResult = IncomeStructureSchema.omit({
    assets: true,
    introduction: true,
    remark: true,
  }).safeParse(incomeStructure);

  return formResult.success;
};

const checkIntroRemarkCompleted = (
  type: 'introduction' | 'remark',
  language: 'th' | 'en',
  data?: IntroductionFormValues | RemarkFormValues
) => {
  const schemaType =
    type === 'introduction' ? IntroductionSchema : RemarkSchema;
  const schema =
    language === 'th'
      ? schemaType.omit({ descriptionEn: true })
      : schemaType.omit({ descriptionTh: true });

  const resault = schema.optional().nullable().safeParse(data);

  return resault.success;
};

export const checkIncomeCategoryNameCompleted = (
  incomeCategory?: IIncomeCategoryFormValues,
  language?: 'th' | 'en'
) => {
  let schema;

  switch (language) {
    case 'th':
      schema = IncomeCategorySchema.omit({ nameEn: true });
      break;
    case 'en':
      schema = IncomeCategorySchema.omit({ nameTh: true });
      break;
    default:
      schema = IncomeCategorySchema;
  }

  return schema.safeParse(incomeCategory).success;
};

export const enabledIncomeStructureV2CompletedSectionTh = (
  incomeStructure?: IIncomeStructureFormValues
): boolean => {
  const isFormCompleted = checkIncomeStructureCompleted(incomeStructure);
  const isValidNameTh = checkNameValidTh(incomeStructure);
  const isValidAssetsTh = checkAssetsValidTh(incomeStructure);
  const isValidIntroductionTh = checkIntroRemarkCompleted(
    'introduction',
    'th',
    incomeStructure?.introduction
  );
  const isValidRemarkTh = checkIntroRemarkCompleted(
    'remark',
    'th',
    incomeStructure?.remark
  );

  return (
    isFormCompleted &&
    isValidNameTh &&
    isValidAssetsTh &&
    isValidIntroductionTh &&
    isValidRemarkTh
  );
};

export const enabledIncomeStructureV2CompletedSectionEn = (
  incomeStructure?: IIncomeStructureFormValues
): boolean => {
  const isFormCompleted = checkIncomeStructureCompleted(incomeStructure);
  const isValidNameEn = checkNameValidEn(incomeStructure);
  const isValidAssetsEn = checkAssetsValidEn(incomeStructure);
  const isValidIntroductionEn = checkIntroRemarkCompleted(
    'introduction',
    'en',
    incomeStructure?.introduction
  );
  const isValidRemarkEn = checkIntroRemarkCompleted(
    'remark',
    'en',
    incomeStructure?.remark
  );

  return (
    isFormCompleted &&
    isValidNameEn &&
    isValidAssetsEn &&
    isValidIntroductionEn &&
    isValidRemarkEn
  );
};

export const sumExtendedValues = (
  formValues: Record<string, IIncomeItemFormValues>,
  subgroup: INCOME_SUBGROUP_CATEGORY,
  yearField: keyof IIncomeItemFormValues
): string => {
  let total = 0;

  const extendedItems = Object.keys(formValues).filter((key) =>
    key.startsWith(subgroup)
  );

  if (extendedItems.length === 0) {
    return '';
  }

  const hasEmptyValue = extendedItems.some((key) => {
    const value = formValues[key]?.[yearField];
    if (!value || value === '') return true;

    total += Number(value) || 0;
    return false;
  });

  if (hasEmptyValue) {
    return '';
  }

  return total.toString();
};

export const setEmptyYearFields = (prevIncomeItem: IIncomeItemFormValues) => {
  return {
    ...prevIncomeItem,
    asOfYear: '',
    asOfYesteryear: '',
    asOfYearBeforeYesteryear: '',
  };
};

export const calcNetIncomeItem = (
  totalValue: string,
  extendedValues: string
): string => (Number(totalValue) - Number(extendedValues)).toString();

export const calcIncomeItemPct = (
  value: string,
  totalValue: string
): string => {
  const numValue = Number(value);
  const numTotal = Number(totalValue);

  if (isNaN(numValue) || isNaN(numTotal) || numTotal <= 0) {
    return numTotal === 0 ? '0' : '';
  }

  return ((numValue / numTotal) * 100).toString();
};
